import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Crown, Plus, TriangleAlert } from "lucide-react";

import { apiError } from "../../shared/api/client";
import {
  membershipApi,
  membershipQueryKeys,
  type MembershipTier,
  type TierForm,
} from "../../shared/api/membership";
import { formatVndWithUnit } from "../../shared/format";
import { PageHeader } from "../../shared/ui/PageHeader";
import { EntitlementGate } from "../../shared/entitlement/EntitlementGate";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

/**
 * Cấu hình hạng thành viên (#18734) — the clinic's own spend ladder.
 *
 * <p>Pro-only, so the whole page sits behind {@link EntitlementGate}; the API answers 402 on Lite
 * regardless.
 *
 * <p>The thresholds shipped in the migration are placeholders. This screen exists so a clinic sets
 * its own: a dental practice and a general practice differ by an order of magnitude in what a
 * patient spends, and one hardcoded ladder was wrong for nearly everyone.
 *
 * <p>Until the clinic edits or confirms them, they stay marked as a suggestion and no patient is
 * shown a tier (#18871). The banner is not decoration — it is the only place anyone is told that
 * the feature is deliberately silent, and the one-click "dùng ngưỡng gợi ý này" is what keeps it
 * from being silent forever for a clinic that agrees with the numbers.
 */
export function MembershipSettingsPage() {
  return (
    <EntitlementGate code="module.membership">
      <MembershipSettingsContent />
    </EntitlementGate>
  );
}

function MembershipSettingsContent() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<MembershipTier | null>(null);
  const [creating, setCreating] = useState(false);

  const tiers = useQuery({
    queryKey: membershipQueryKeys.tiers(),
    queryFn: () => membershipApi.tiers(),
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => membershipApi.deactivateTier(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: membershipQueryKeys.tiers() }),
  });

  const confirm = useMutation({
    mutationFn: () => membershipApi.confirmTiers(),
    onSuccess: () => qc.invalidateQueries({ queryKey: membershipQueryKeys.tiers() }),
  });

  // Một bậc chưa xác nhận là đủ để cả bậc thang chưa dùng được: ngưỡng của bậc trên quyết định
  // bậc dưới kết thúc ở đâu. Backend tính đúng như vậy (MembershipService.provisional).
  const provisional = !!tiers.data?.some((t) => t.confirmedAt === null);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Hạng thành viên"
        description="Hạng được tính theo TỔNG SỐ TIỀN bệnh nhân đã chi — tự động từ hoá đơn, không nhập tay."
        actions={
          <Button onClick={() => { setCreating(true); setEditing(null); }}>
            <Plus className="mr-1.5 size-4" />
            Thêm hạng
          </Button>
        }
      />

      {provisional && (
        <Card className="border-amber-300 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/40">
          <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start">
            <TriangleAlert className="mt-0.5 size-5 shrink-0 text-amber-600" />
            <div className="min-w-0 flex-1 space-y-1.5 text-sm">
              <p className="font-semibold">Ngưỡng dưới đây là gợi ý, chưa phải chính sách của phòng khám.</p>
              <p className="text-muted-foreground">
                Các mức 5/20/50 triệu do hệ thống đặt sẵn khi mở tính năng, không dựa trên số liệu
                của phòng khám. Vì vậy <strong>bệnh nhân chưa nhìn thấy hạng của mình</strong> — hệ
                thống không thay phòng khám hứa một quyền lợi chưa được duyệt. Nhân viên vẫn xem
                được để đối chiếu.
              </p>
              <p className="text-muted-foreground">
                Sửa lại cho đúng mức chi tiêu thực tế, hoặc xác nhận nếu thấy dùng được — sau đó
                hạng sẽ hiện cho bệnh nhân.
              </p>
            </div>
            <Button
              className="shrink-0"
              onClick={() => confirm.mutate()}
              disabled={confirm.isPending}
            >
              {confirm.isPending ? "Đang lưu…" : "Dùng ngưỡng gợi ý này"}
            </Button>
          </CardContent>
        </Card>
      )}

      {(creating || editing) && (
        <TierFormCard initial={editing} onDone={() => { setCreating(false); setEditing(null); }} />
      )}

      <Card>
        <CardContent className="p-0">
          {tiers.isLoading && <Skeleton className="m-4 h-24" />}
          {tiers.isError && (
            <p className="p-4 text-sm text-destructive">{apiError(tiers.error).detail}</p>
          )}
          {tiers.data?.length === 0 && (
            <div className="flex flex-col items-center gap-2 p-10 text-center text-muted-foreground">
              <Crown className="size-8" />
              <p className="text-sm">
                Chưa cấu hình hạng nào. Khi chưa có bậc thang, bệnh nhân sẽ không có hạng — hệ thống
                không tự bịa ra một hạng nào cả.
              </p>
            </div>
          )}
          {!!tiers.data?.length && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Mã</TableHead>
                  <TableHead>Tên hạng</TableHead>
                  <TableHead>Chi tiêu tối thiểu</TableHead>
                  <TableHead>Thứ tự</TableHead>
                  <TableHead className="text-right">Thao tác</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tiers.data.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="font-mono">{t.code}</TableCell>
                    <TableCell>
                      {t.name} {!t.active && <Badge variant="secondary">Đã tắt</Badge>}
                      {t.confirmedAt === null && (
                        <Badge variant="outline" className="ml-1.5">
                          Gợi ý
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="font-mono tabular-nums">
                      {formatVndWithUnit(t.minSpendVnd)}
                    </TableCell>
                    <TableCell>{t.rankOrder}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => { setEditing(t); setCreating(false); }}>
                        Sửa
                      </Button>
                      {t.active && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive"
                          onClick={() => deactivate.mutate(t.id)}
                          disabled={deactivate.isPending}
                        >
                          Tắt
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        Hạng tính từ tổng tiền đã thanh toán (không tính hoá đơn đã huỷ hoặc đã hoàn tiền, và không
        tính phần BHYT chi trả). Khách chuyển từ phần mềm cũ sang có thể được ghi nhận chi tiêu cũ ở
        trang hồ sơ bệnh nhân.
      </p>
    </div>
  );
}

function TierFormCard({ initial, onDone }: { initial: MembershipTier | null; onDone: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<TierForm>(() =>
    initial
      ? {
          code: initial.code,
          name: initial.name,
          minSpendVnd: initial.minSpendVnd,
          rankOrder: initial.rankOrder,
          active: initial.active,
        }
      : { code: "", name: "", minSpendVnd: 0, rankOrder: 1, active: true }
  );

  const save = useMutation({
    mutationFn: () =>
      initial ? membershipApi.updateTier(initial.id, form) : membershipApi.createTier(form).then(() => undefined),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: membershipQueryKeys.tiers() });
      onDone();
    },
  });

  const set = <K extends keyof TierForm>(k: K, v: TierForm[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  return (
    <Card>
      <CardContent className="space-y-4 p-5">
        <h3 className="text-sm font-semibold">
          {initial ? `Sửa hạng ${initial.code}` : "Hạng mới"}
        </h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor="code">Mã hạng</Label>
            <Input
              id="code"
              value={form.code}
              disabled={!!initial}
              placeholder="GOLD"
              onChange={(e) => set("code", e.target.value.toUpperCase())}
            />
            {initial && (
              <p className="mt-1 text-xs text-muted-foreground">
                Mã không sửa được — đã nói với khách một tên hạng thì tên đó phải giữ nguyên ý nghĩa.
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="name">Tên hiển thị</Label>
            <Input
              id="name"
              value={form.name}
              placeholder="Vàng"
              onChange={(e) => set("name", e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="min">Chi tiêu tối thiểu (₫)</Label>
            <Input
              id="min"
              type="number"
              min={0}
              value={form.minSpendVnd}
              onChange={(e) => set("minSpendVnd", Number(e.target.value) || 0)}
            />
          </div>
          <div>
            <Label htmlFor="rank">Thứ tự (số lớn = hạng cao)</Label>
            <Input
              id="rank"
              type="number"
              min={1}
              value={form.rankOrder}
              onChange={(e) => set("rankOrder", Number(e.target.value) || 1)}
            />
          </div>
        </div>
        {save.isError && <p className="text-sm text-destructive">{apiError(save.error).detail}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onDone}>Huỷ</Button>
          <Button
            onClick={() => save.mutate()}
            disabled={save.isPending || !form.code.trim() || !form.name.trim()}
          >
            {save.isPending ? "Đang lưu…" : "Lưu"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
